/**
 * SuppliesSection.tsx — THE SUPPLIES PART OF THE PRESCRIPTION BUILDER.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescription supplies
 * (2026-09-24, CTO + clinical lead).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE INCIDENT. A live patient received her pens with nothing to use them with.
 * The clinician had written the requirement down — in the free-text NOTES, which
 * never reach the pharmacy's dispensing email and print in grey 9pt at the foot of
 * the PDF. Nothing was wrong with the clinician's judgement; the product gave the
 * instruction nowhere a pharmacist would read it.
 *
 * THE RULING. The pen supply goes with every injectable prescription by default;
 * a sharps bin with a patient's FIRST prescription by default. The clinician can
 * remove either, and can add either to ANY prescription (tablets included — this
 * is never restricted by treatment). The pharmacy must be unable to miss them.
 *
 * WHAT THIS SECTION DOES WITH THAT:
 *  - PRELOADS what orders-service suggests (`suggestedSupplies`) — the rule lives
 *    server-side, once (R41); this file does not know what makes a treatment
 *    injectable or a prescription a first one, and must not learn. On a RE-ISSUE
 *    the rows start from what the case's latest prescription carried
 *    (`priorSupplies`, at ITS quantities), and then every suggestion that list
 *    does not already carry is added — so a supply removed last time comes back
 *    as a suggestion while the server still suggests it (e.g. a bin for a
 *    patient who is still first-time). See `supplyRows.ts#seedSupplyRows`.
 *  - Each row: the server's NAME, an editable whole-number quantity (1..50), the
 *    reason it was suggested in words, and Remove.
 *  - "Add a supply" offers every catalogue entry not already on the list.
 *  - An EMPTIED list is a decision, and it is posted as one (`supplies: []`).
 *
 * ⚠️ AND A FAILED READ BLOCKS THE ISSUE CONTROL. If the catalogue cannot be read
 * this section says so, offers a retry, and the form will not issue — exactly
 * the posture of a failed licensed-strength read. A form that quietly showed "no
 * supplies" during an outage would reproduce the incident with a green button.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * R41: the browser posts `{ supplyKey, quantity }` and nothing else. The server
 * resolves the key and snapshots the name, unit and repeat rule IT holds.
 *
 * R28: not one supply name exists in this file. The names on screen arrive from
 * the API, and the reason copy for the pen supply says "injectable treatment".
 *
 * MONEY-FREE: no price, cost or plan field exists on any supply shape (the
 * pricing is agreed separately with the pharmacy), so there is nothing to redact.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { SupplyCatalogueEntry } from '../api/types';
import { Alert, Button, Field, Help, Select, Spinner, TextInput } from '../ui/kit';
import { RequiredMark } from '../ui/RequiredMark';
import type { PrescribingContextState } from './usePrescribingContext';
import {
  MAX_SUPPLIES,
  MAX_SUPPLY_QUANTITY,
  MIN_SUPPLY_QUANTITY,
  supplyQuantityValid,
  type SupplyDraft,
  type SupplyRowReason,
} from './supplyRows';

/**
 * The one sentence per reason a row starts on the list. Exported so the specs
 * assert the real strings. `previous_prescription` is a RE-ISSUE's row, carried
 * over from what this case's latest prescription went out with.
 */
export const SUPPLY_REASON_COPY: Record<SupplyRowReason, string> = {
  injectable: 'Suggested: injectable treatment',
  first_prescription: 'Suggested: first prescription',
  previous_prescription: 'From the previous prescription',
};

/** The copy shown where the section cannot load — one sentence, one place. */
export const SUPPLIES_UNAVAILABLE =
  'The supplies list could not be loaded, so this prescription cannot be issued yet.';

/** The line shown when the clinician has removed every supply. */
export const NO_SUPPLIES_COPY = 'No supplies will be sent with this prescription.';

/**
 * The row's reason, in words. An unknown reason (a newer orders build) still
 * says the row was suggested — it never invents WHY.
 */
function reasonCopy(reason: SupplyRowReason | null): string | null {
  if (reason === null) return null;
  return SUPPLY_REASON_COPY[reason] ?? 'Suggested';
}

/** One row: the name, the quantity, the reason, Remove. */
function SupplyRow({
  inputId,
  draft,
  entry,
  disabled,
  onQuantity,
  onRemove,
}: {
  /** Owned by the section, so it can move focus to a row it has just added. */
  inputId: string;
  draft: SupplyDraft;
  entry: SupplyCatalogueEntry;
  disabled: boolean;
  onQuantity: (quantityText: string) => void;
  onRemove: () => void;
}) {
  const errorId = useId();
  const reasonId = useId();
  const invalid = !supplyQuantityValid(draft.quantityText);
  const reason = reasonCopy(draft.reason);
  const describedBy = [reason ? reasonId : null, invalid ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto',
        alignItems: 'end',
        gap: '8px 12px',
        padding: '10px 0',
        borderBottom: '1px solid var(--gps-border)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <p style={{ margin: '0 0 2px', fontWeight: 600, color: 'var(--gps-text)', overflowWrap: 'anywhere' }}>{entry.name}</p>
        {reason ? (
          <p id={reasonId} style={{ margin: '0 0 6px', fontSize: '12.5px', color: 'var(--gps-muted)' }}>
            {reason}
          </p>
        ) : null}
        {/* A real <label>, bound by id. The visible word is "Quantity"; the
            supply's name rides in a screen-reader-only span INSIDE the label,
            so every quantity box on the list has a distinct accessible name
            that still starts with the visible text (WCAG 2.5.3). */}
        <label
          htmlFor={inputId}
          style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)', marginBottom: '4px' }}
        >
          Quantity<span className="gps-sr-only"> of {entry.name}</span>
          {/* REQUIRED WHILE THE ROW IS ON THE LIST (2026-09-25). The section is
              optional — Remove takes a supply off — but a supply that is sent
              needs a quantity, and an empty or out-of-range one blocks Issue.
              The "*" follows the screen-reader-only name, so the label's text
              reads "Quantity of <name>*" and its accessible name stays
              "Quantity of <name>"; `aria-required` below is what is announced. */}
          <RequiredMark />
        </label>
        {/* TEXT with a numeric keypad, not type="number": a number input
            silently swallows non-numeric keystrokes, changes value on a scroll
            wheel, and would make this the prescribe form's only spinbutton — a
            control the medication lines deliberately do not have. */}
        <TextInput
          id={inputId}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          value={draft.quantityText}
          disabled={disabled}
          aria-required="true"
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onChange={(e) => onQuantity(e.target.value)}
          style={{ maxWidth: '120px' }}
        />
        {invalid ? (
          <p id={errorId} style={{ margin: '4px 0 0', fontSize: '12.5px', fontWeight: 500, color: 'var(--gps-danger-text)' }}>
            Enter a whole number from {MIN_SUPPLY_QUANTITY} to {MAX_SUPPLY_QUANTITY}.
          </p>
        ) : null}
      </div>
      <Button
        type="button"
        variant="ghost"
        onClick={onRemove}
        disabled={disabled}
        style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
      >
        Remove<span className="gps-sr-only"> {entry.name}</span>
      </Button>
    </li>
  );
}

/**
 * The Supplies section. Controlled: the surface owns `rows` (it has to post
 * them — see `useSupplyRows`), this component owns only the transient "which
 * entry is chosen in the Add select" state and where focus goes next.
 *
 * `rows === null` means the section has not been seeded from the server yet —
 * the surface keeps its submit disabled until it has.
 */
/**
 * The ids a section rendered with `idBase` uses — ONE scheme, read by the
 * section and by the form's error summary, so a "quantity of <supply>" link
 * cannot aim at a box that is named differently (2026-09-25).
 */
export function supplySectionIds(idBase: string) {
  return {
    section: idBase,
    retry: `${idBase}-retry`,
    add: `${idBase}-add`,
    quantity: (supplyKey: string) => `${idBase}-qty-${supplyKey}`,
  };
}

export function SuppliesSection({
  context,
  rows,
  disabled,
  onChange,
  onRetry,
  idBase,
}: {
  context: PrescribingContextState;
  rows: SupplyDraft[] | null;
  disabled: boolean;
  onChange: (rows: SupplyDraft[]) => void;
  onRetry: () => void;
  /**
   * A caller-chosen id prefix, so the form's error summary can link to this
   * section's controls (see {@link supplySectionIds}). Absent → a generated
   * one, exactly as before.
   */
  idBase?: string;
}) {
  const generatedId = useId();
  const ids = supplySectionIds(idBase ?? generatedId);
  const addId = ids.add;
  const quantityId = ids.quantity;
  const [toAdd, setToAdd] = useState('');
  const rootRef = useRef<HTMLFieldSetElement>(null);

  /* ── FOCUS NEVER FALLS TO <body> (WCAG 2.4.3) ──────────────────────────────
     Three presses REMOVE the control that was pressed, and each would otherwise
     drop the keyboard to the top of the document in the middle of a clinical
     form:
       · Remove — the row goes; focus moves to the Add select, where the removed
         supply now reappears.
       · Add supply — the button disables (nothing is chosen any more) or the
         whole add block unmounts at the last catalogue entry; focus moves to the
         NEW row's quantity box, the thing the clinician adjusts next.
       · Retry loading supplies — the alert holding it is replaced by the
         spinner; focus moves to the section itself (below), which survives
         every state because each branch renders the same <fieldset>.
     The first two land AFTER the parent re-renders the rows, so the target id is
     parked here and focused by an effect that runs after every render. By id,
     because the kit's controls do not forward refs. */
  const pendingFocus = useRef<string | null>(null);
  useEffect(() => {
    const target = pendingFocus.current;
    if (!target) return;
    const element = document.getElementById(target);
    if (!element) return;
    pendingFocus.current = null;
    element.focus();
  });

  function retry() {
    // Focus the section BEFORE the alert holding this button is swapped for
    // the spinner. `tabIndex={-1}` makes the fieldset focusable by script only
    // — it is not added to the tab order.
    rootRef.current?.focus();
    onRetry();
  }

  const frame = (children: ReactNode) => (
    <fieldset
      ref={rootRef}
      id={ids.section}
      tabIndex={-1}
      style={{
        border: '1px solid var(--gps-border-strong)',
        borderRadius: 'var(--gps-r-md)',
        padding: 'var(--gps-s-3)',
        margin: '0 0 var(--gps-s-3)',
        minWidth: 0,
        outline: 'none',
      }}
    >
      <legend style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gps-text)', padding: '0 6px' }}>Supplies</legend>
      <Help>
        Sent to the pharmacy as part of this prescription — printed on it and in the dispensing email. Remove any that
        are not wanted, or add one.
      </Help>
      {children}
    </fieldset>
  );

  if (context.state === 'loading' || (context.state === 'ready' && rows === null)) {
    return frame(<Spinner label="Loading the supplies list…" />);
  }

  if (context.state === 'error') {
    // DANGER, NAMES THE BLOCK, OFFERS A REAL RETRY — the same posture as the
    // licensed-strength list (`VariantPicker`), because this is the same kind
    // of failure: a prescription issued past it would go out without something
    // the prescriber meant to send.
    return frame(
      <Alert tone="danger" role="alert">
        <strong style={{ display: 'block', fontWeight: 600 }}>{SUPPLIES_UNAVAILABLE}</strong>
        <span style={{ display: 'block', marginTop: '4px' }}>
          Nothing has been changed. Supplies travel with the prescription itself, so it is not issued without this
          list. Try again, and if it keeps failing, tell the team rather than working around it.
        </span>
        <Button
          id={ids.retry}
          type="button"
          variant="ghost"
          onClick={retry}
          style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', marginTop: '10px' }}
        >
          Retry loading supplies
        </Button>
      </Alert>,
    );
  }

  const { catalogue } = context.context;
  const current = rows ?? [];
  const byKey = new Map(catalogue.map((entry) => [entry.supplyKey, entry]));
  const addable = catalogue.filter((entry) => !current.some((row) => row.supplyKey === entry.supplyKey));
  const atCap = current.length >= MAX_SUPPLIES;

  function patchRow(index: number, quantityText: string) {
    onChange(current.map((row, i) => (i === index ? { ...row, quantityText } : row)));
  }

  function removeRow(index: number) {
    pendingFocus.current = addId;
    onChange(current.filter((_, i) => i !== index));
  }

  function addChosen() {
    if (!toAdd || current.some((row) => row.supplyKey === toAdd) || atCap) return;
    pendingFocus.current = quantityId(toAdd);
    onChange([...current, { supplyKey: toAdd, quantityText: String(MIN_SUPPLY_QUANTITY), reason: null }]);
    setToAdd('');
  }

  return frame(
    <>
      {current.length === 0 ? (
        // STATED, not left blank: an empty list is the clinician's decision, and
        // the words confirm it is the one they meant to make.
        <p role="status" style={{ margin: '0 0 var(--gps-s-2)', fontSize: '13.5px', color: 'var(--gps-text)' }}>
          {NO_SUPPLIES_COPY}
        </p>
      ) : (
        <ul aria-label="Supplies to send" style={{ listStyle: 'none', margin: '0 0 var(--gps-s-2)', padding: 0 }}>
          {current.map((row, index) => {
            const entry = byKey.get(row.supplyKey);
            // Unreachable by construction — rows are seeded from suggestions or
            // prior supplies the read has already matched against the
            // catalogue, and added from the catalogue itself. A row with no
            // entry has no name we may show.
            if (!entry) return null;
            return (
              <SupplyRow
                key={row.supplyKey}
                inputId={quantityId(row.supplyKey)}
                draft={row}
                entry={entry}
                disabled={disabled}
                onQuantity={(text) => patchRow(index, text)}
                onRemove={() => removeRow(index)}
              />
            );
          })}
        </ul>
      )}

      {addable.length > 0 && !atCap ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '8px 12px' }}>
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <Field label="Add a supply" htmlFor={addId}>
              <Select
                id={addId}
                value={toAdd}
                disabled={disabled}
                onChange={(e) => setToAdd(e.target.value)}
              >
                <option value="">Choose a supply…</option>
                {addable.map((entry) => (
                  <option key={entry.supplyKey} value={entry.supplyKey}>
                    {entry.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {/* A separate Add button rather than adding on the select's change:
              some browsers fire `change` for every arrow key on a closed select,
              which would add supplies a keyboard user was only scrolling past. */}
          <Button
            type="button"
            variant="ghost"
            onClick={addChosen}
            disabled={disabled || toAdd === ''}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', marginBottom: 'var(--gps-s-3)' }}
          >
            Add supply
          </Button>
        </div>
      ) : (
        <Help style={{ marginBottom: 0 }}>
          {atCap
            ? `A prescription can carry up to ${MAX_SUPPLIES} supplies.`
            : 'Every supply on the list has been added.'}
        </Help>
      )}
    </>,
  );
}
